import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { Section } from "./_Section";
import * as yup from "yup";
import { clearMask, formatDate, formatDateToAPI, formatDateToBR, showToast, cepMask } from "../../utils";
import { 
  Flex, 
  Button, 
  Text, 
  VStack, 
  HStack,
  useDisclosure 
} from "@chakra-ui/react";
import { Input } from "../../components/Forms/Input";
import { DocumentInput } from "../../components/Forms/DocumentInput";
import { SelectButtonV2 } from "../../components/Forms/SelectButtonV2";
import { useSeller } from "../../hooks/useSeller";
import { useEffect, useState, useRef } from "react";
import { Table } from "antd";
import { Alert } from "../../components/Alert";
import { FormModal } from "../../components/Forms/FormModal";
import { isValidCpfOrCnpj, checkIsMinor } from "../../utils/validationDocument";

const ownerFormSchema = yup.object().shape({
  name: yup.string().required("Nome é obrigatório").max(255, "Nome deve ter até 255 caracteres"),
  document: yup.string().required("CPF/CNPJ é obrigatório").test("is-valid-document", "CPF ou CNPJ inválido", (value) => {
    if (!value) return false;
    return isValidCpfOrCnpj(value);
  }),
  birthdate: yup.string().required("Data de nascimento é obrigatória"),
  partnerType: yup.string(),
  motherName: yup.string().when("birthdate", {
    is: (birthdate: string) => checkIsMinor(birthdate),
    then: (schema) => schema.required("Nome da mãe é obrigatório"),
    otherwise: (schema) => schema.optional(),
  }),
  address: yup.object().shape({
    zipCode: yup.string().min(8, "CEP inválido"),
    street: yup.string(),
    number: yup.string(),
    complement: yup.string(),
    district: yup.string(),
    city: yup.string(),
    state: yup.string().min(2, "Estado é obrigatório"),
  }),
});

interface Owner {
  id: number;
  name: string;
  document: string;
  birthdate: string;
  motherName?: string;
  partnerType: string;
  address: {
    country: string;
    state: string;
    city: string;
    district: string;
    street: string;
    zipCode: string;
    number: string;
    complement?: string;
  };
}

interface OwnersSectionProps {
  id: string | undefined;
}

export function OwnersSection({ id }: OwnersSectionProps) {
  const { getOwners, postOwner, patchOwner, deleteOwner, consultCep } = useSeller();
  const [owners, setOwners] = useState<Owner[]>([]);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCepLoading, setIsCepLoading] = useState(false);
  const [deleteModalState, setDeleteModalState] = useState(false);
  const [ownerToDelete, setOwnerToDelete] = useState<Owner | null>(null);
  const [editingOwner, setEditingOwner] = useState<Owner | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const previousCepRef = useRef<string>("");
  const isModalJustOpenedRef = useRef<boolean>(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    setError,
    clearErrors,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(ownerFormSchema),
  });

  const cep = watch("address.zipCode");
  const birthdate = watch("birthdate");
  const cleanedDoc = clearMask(watch("document") ?? "");
  const isMinor = checkIsMinor(birthdate);
  const label = ((type: string) => { 
    const formattedLabel = {
      name: cleanedDoc.length === 14 ? "Razão social*" : "Nome completo*",
      birthdate: cleanedDoc.length === 14 ? "Data de abertura da empresa*" : "Data de nascimento*",
    }
    return formattedLabel[type] || "";
  });
  const placeholder = ((type: string) => {
    const formattedPlaceholder = {
      name: cleanedDoc.length === 14 ? "Razão social da empresa" : "João Silva",
      birthdate: cleanedDoc.length === 14 ? "00/00/0000" : "01/01/2001",
    }
    return formattedPlaceholder[type] || "";
  });
  useEffect(() => {
    loadOwners();
    //eslint-disable-next-line
  }, [id]);

  async function loadOwners() {
    if (!id) return;
    try {
      setLoading(true);
      const response = await getOwners(id);
      setOwners(response?.data || []);
    } catch (error: any) {
      error?.response?.data?.errors?.map((error: any) =>
        showToast({
          type: "error",
          message: `${error?.code} - ${error?.description}`,
        })
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const cepCleaned = clearMask(cep || "");
    const previousCepCleaned = clearMask(previousCepRef.current || "");
    if (
      cepCleaned.length >= 8 && 
      cepCleaned !== previousCepCleaned &&
      !isModalJustOpenedRef.current
    ) {
      handleGetCep();
    }
    
    // Atualiza a referência do CEP anterior
    previousCepRef.current = cep || "";
    
    if (isModalJustOpenedRef.current) {
      setTimeout(() => {
        isModalJustOpenedRef.current = false;
      }, 100);
    }
    //eslint-disable-next-line
  }, [cep]);

  async function handleGetCep() {
    try {
      clearErrors("address.zipCode");
      setIsCepLoading(true);
      const { data } = await consultCep(clearMask(cep));
      setValue("address.street", data.logradouro);
      setValue("address.district", data.bairro);
      setValue("address.city", data.localidade);
      setValue("address.state", data.uf);
      setValue("address.complement", data.complemento);
    } catch {
      if (cep.length !== 10) {
        setError("address.zipCode", { type: "custom", message: "Cep inválido." });
      }
    } finally {
      setIsCepLoading(false);
    }
  }


  function handleOpenCreateModal() {
    setEditingOwner(null);
    reset();
    setValue("address.country", "BR");
    previousCepRef.current = "";
    isModalJustOpenedRef.current = true;
    onOpen();
  }

  function handleOpenEditModal(owner: Owner) {
    setEditingOwner(owner);
    const defaultValues = {
      name: owner?.name ?? "",
      document: owner?.document ?? "",
      birthdate: owner?.birthdate ? formatDateToBR(owner.birthdate) : "",
      ...(owner?.motherName ? { motherName: owner.motherName } : {}),
      partnerType: owner?.partnerType ?? "",
      address: {
        zipCode: owner?.address?.zipCode ? cepMask(owner.address.zipCode) : "",
        street: owner?.address?.street ?? "",
        number: owner?.address?.number ?? "",
        complement: owner?.address?.complement ?? "",
        district: owner?.address?.district ?? "",
        city: owner?.address?.city ?? "",
        state: owner?.address?.state ?? "",
        country: owner?.address?.country ?? "BR",
      },
    };
    reset(defaultValues);
    // Armazena o CEP atual para evitar busca ao abrir o modal
    previousCepRef.current = defaultValues.address.zipCode || "";
    isModalJustOpenedRef.current = true;
    onOpen();
  }


  function handleOpenDeleteModal(owner: Owner) {
    setOwnerToDelete(owner);
    setDeleteModalState(true);
  }

  async function onSubmit(data: any) {
    if (!id) return;
    try {
      setIsSubmitting(true);
      const ownerData = {
        name: data.name,
        document: clearMask(data.document),
        birthdate: formatDateToAPI(data.birthdate),
        motherName: isMinor ? data.motherName : undefined,
        partnerType: data.partnerType || undefined,
        address: data.address.zipCode ? {
          zipCode: clearMask(data.address.zipCode),
          street: data.address.street,
          number: data.address.number,
          complement: data.address.complement,
          district: data.address.district,
          city: data.address.city,
          state: data.address.state,
        } : undefined,
      };

      if (editingOwner) {
        await patchOwner(id, editingOwner.id.toString(), ownerData);
        showToast({
          type: "success",
          message: "Sócio atualizado com sucesso!",
        });
      } else {
        await postOwner(id, ownerData);
        showToast({
          type: "success",
          message: "Sócio cadastrado com sucesso!",
        });
      }

      onClose();
      reset();
      await loadOwners();
    } catch (error: any) {
      error?.response?.data?.errors?.map((error: any) =>
        showToast({
          type: "error",
          message: `${error?.code} - ${error?.description}`,
        })
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleDelete() {
    if (!id || !ownerToDelete) return;
    try {
      setIsSubmitting(true);
      await deleteOwner(id, ownerToDelete.id.toString());
      showToast({
        type: "success",
        message: "Sócio excluído com sucesso!",
      });
      setDeleteModalState(false);
      setOwnerToDelete(null);
      await loadOwners();
    } catch (error: any) {
      error?.response?.data?.errors?.map((error: any) =>
        showToast({
          type: "error",
          message: `${error?.code} - ${error?.description}`,
        })
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  const columns: any[] = [
    {
      key: "name",
      title: "Nome",
      dataIndex: "name",
      render: (name: string) => <Text>{name}</Text>,
    },
    {
      key: "document",
      title: "CPF",
      dataIndex: "document",
      render: (document: string) => <Text>{document}</Text>,
    },
    {
      key: "birthdate",
      title: "Data de Nascimento",
      dataIndex: "birthdate",
      render: (birthdate: string) => <Text>{formatDate(birthdate)}</Text>,
    },
    {
      key: "partnerType",
      title: "Tipo",
      dataIndex: "partnerType",
      render: (partnerType: string) => (
        <Text>
          {partnerType === "PARTNER" ? "Sócio" :
           partnerType === "PROXY_HOLDER" ? "Procurador" :
           partnerType === "LEGAL_REPRESENTATIVE" ? "Representante Legal" :
           partnerType === "OTHER" ? "Outros" :
           partnerType || "-"}
        </Text>
      ),
    },
    {
      key: "actions",
      title: "Ações",
      render: (_, record: Owner) => (
        <Flex gap="8px">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => handleOpenEditModal(record)}
          >
            Editar
          </Button>
          <Button
            size="sm"
            variant="cancel"
            onClick={() => handleOpenDeleteModal(record)}
          >
            Excluir
          </Button>
        </Flex>
      ),
    },
  ];

  return (
    <>
      <Alert
        isOpen={deleteModalState}
        onClose={() => {
          setDeleteModalState(false);
          setOwnerToDelete(null);
        }}
        title="Excluir sócio?"
        messageBody={`Deseja excluir o sócio ${ownerToDelete?.name}?`}
        confirmButtonText="Excluir"
        closeButtonText="Cancelar"
        onConfirm={handleDelete}
        leastDestructiveRef={null}
        isLoading={isSubmitting}
      />
      <FormModal
        title={editingOwner ? "Editar Sócio" : "Adicionar Sócio"}
        isOpen={isOpen}
        onClose={() => {
          onClose();
          reset();
          setEditingOwner(null);
          previousCepRef.current = "";
          isModalJustOpenedRef.current = false;
        }}
        handleConfirmModal={handleSubmit(onSubmit)}
        isSubmitting={isSubmitting}
        width="600px"
        confirmationButtonText={editingOwner ? "Salvar" : "Cadastrar"}
        isAllowToSubmit
      >
        <VStack spacing={4} align="stretch">
          <Flex w="100%" gap="4">
            <DocumentInput
              label="CPF/CNPJ*"
              type="text"
              name="document"
              placeholder="Digite o CPF/CNPJ"
              mask="cpf/cnpj"
              register={register("document")}
              errors={errors.document}
            />
            <Input
              label={label("name")}
              type="text"
              name="name"
              maxLength={255}
              placeholder={placeholder("name")}
              register={register("name")}
              errors={errors.name}
            />

          </Flex>

          <Flex w="100%" gap="4">
            <Input
              label={label("birthdate")}
              type="text"
              maxLength={10}
              name="birthdate"
              placeholder={placeholder("birthdate")}
              register={register("birthdate")}
              mask="date"
              errors={errors.birthdate}
            />
            <SelectButtonV2
              name="partnerType"
              label="Tipo de sócio"
              variant="ghost"
              bgColor="#EDF1F7"
              h="27px"
              register={{ ...register("partnerType") }}
              errors={errors.partnerType}
            >
              <option value="PARTNER">Sócio</option>
              <option value="PROXY_HOLDER">Procurador</option>
              <option value="LEGAL_REPRESENTATIVE">Representante Legal</option>
              <option value="OTHER">Outros</option>
            </SelectButtonV2>
          </Flex>

          {isMinor && (
            <Input
              label="Nome da mãe*"
              type="text"
              name="motherName"
              maxLength={255}
              placeholder="Nome completo da mãe"
              register={register("motherName")}
              errors={errors.motherName}
            />
          )}

          <Text fontSize="md" fontWeight="medium" mt={2}>
            Endereço do Sócio (Opcional)
          </Text>

          <Input
            label="CEP"
            type="text"
            name="address.zipCode"
            placeholder="00000-000"
            mask="cep"
            register={register("address.zipCode")}
            errors={errors.address?.zipCode}
          />

          <Input
            label="Rua"
            type="text"
            name="address.street"
            placeholder="Avenida Vitória"
            register={register("address.street")}
            errors={errors.address?.street}
            isDisabled={isCepLoading}
          />

          <HStack w="100%" spacing={2}>
            <Input
              label="Número"
              type="number"
              name="address.number"
              placeholder="1234"
              maxLength={6}
              register={register("address.number")}
              errors={errors.address?.number}
              isDisabled={isCepLoading}
            />
            <Input
              label="Complemento"
              type="text"
              name="address.complement"
              maxLength={30}
              placeholder="Sala 12"
              register={register("address.complement")}
              errors={errors.address?.complement}
              isDisabled={isCepLoading}
            />
          </HStack>

          <Input
            label="Bairro"
            type="text"
            name="address.district"
            maxLength={30}
            placeholder="Centro"
            register={register("address.district")}
            errors={errors.address?.district}
            isDisabled={isCepLoading}
          />

          <HStack w="100%" spacing={4}>
            <Input
              label="Cidade"
              type="text"
              name="address.city"
              maxLength={100}
              placeholder="São Paulo"
              register={register("address.city")}
              errors={errors.address?.city}
              isDisabled={isCepLoading}
            />
            <Input
              label="Estado"
              type="text"
              name="address.state"
              textTransform="uppercase"
              maxLength={2}
              placeholder="Ex: SP"
              register={register("address.state")}
              errors={errors.address?.state}
              isDisabled={isCepLoading}
            />
          </HStack>
        </VStack>
      </FormModal>

      <Section
        sectionTitle="Sócios"
        onClickEditButton={() => {}}
        onClickConfirmButton={() => {}}
        buttons={[
          <Button key="add" onClick={handleOpenCreateModal}>
            Adicionar Sócio
          </Button>
        ]}
        isEditButtonHidden
      >
        <Table<Owner>
          columns={columns}
          dataSource={owners}
          rowKey={(record) => record.id}
          loading={loading}
          locale={{ emptyText: "Nenhum sócio cadastrado" }}
        />
      </Section>
    </>
  );
}
